import type { Ssm5DtcModule } from '@packages/domain/ssm5';
import type { StsIconName } from '@packages/ui/shared';

import { colors } from '@packages/core/styles';
import { Button, Typography } from '@packages/ui/shared';
import { DetailViewAside } from '@web/components/detail-view';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

import type { Ssm5DtcFilterKey, Ssm5DtcFilters } from '../helpers/dtc-filters';
import type { Ssm5Dtcs, Ssm5DtcsState } from '../hooks/use-ssm5-dtcs';

import { countDtcs, DTC_FILTERS, filterDtcModules } from '../helpers/dtc-filters';
import { ssm5ControlStyles, ssm5TextStyles } from '../ssm5.styles';
import { Ssm5DtcFilterSwitch } from './ssm5-dtc-filter-switch';
import { Ssm5DtcModuleCard } from './ssm5-dtc-module-card';
import { Ssm5PanelMessage } from './ssm5-panel-message';
import { Ssm5SectionTitle } from './ssm5-section-title';
import { Ssm5TripTime } from './ssm5-trip-time';

/** Props for {@link Ssm5StatusPanel}. */
export type Ssm5StatusPanelProps = {
    /** Whether a failed read can be retried from the panel; a lost connection cannot read again. */
    canRetry: boolean;
    /** The DTC read of the session. */
    dtcs: Ssm5Dtcs;
    /** Which filters of the DTC list are switched on. */
    filters: Ssm5DtcFilters;
    /** Receives the filter and its checked state when a switch is pressed. */
    onFilterChange: (key: Ssm5DtcFilterKey, isChecked: boolean) => void;
    /** Receives the system of a module when its open button is pressed. */
    onOpenModule: (system: string) => void;
    /** Receives a press of the reconnect action, offered after a failure no retry recovers from. */
    onReconnect: () => void;
    /** The VIN of the session, handed to its module cards. */
    vin: string;
};

/** The action a message of the panel body can offer. */
type PanelAction = 'reconnect' | 'retry';

/** What an action of the panel body needs and shows. */
type PanelActionSpec = {
    /** Whether the action needs the connection open; a retry does, a reconnect does not. */
    needsConnection: boolean;
    /** The translation key of the action's title. */
    title: string;
};

/** A message the panel body can show in place of the DTC list. */
type PanelMessage = {
    /** The action the message offers, if any. */
    action?: PanelAction;
    /** The translation key of the text under the title, if any. */
    description?: string;
    /** The icon over the title. */
    icon: StsIconName;
    /** The translation key of the title. */
    title: string;
};

/** Why the panel body lists no DTC. */
type PanelMessageKey = 'filteredOut' | 'loadFailed' | 'noDtcs' | 'unavailable';

/** An action the panel body offers on its message. */
type PanelOffer = {
    /** Carries the action out. */
    handlePress: () => void;
    /** The translation key of the action's title. */
    title: string;
};

/** The actions the panel body can offer. */
const PANEL_ACTIONS: Readonly<Record<PanelAction, PanelActionSpec>> = {
    reconnect: { needsConnection: false, title: 'ssm5:dtcs.unavailable.reconnect' },
    retry: { needsConnection: true, title: 'ssm5:dtcs.loadFailed.retry' },
};

/** The messages of the panel body, by the reason there is no DTC to list. */
const PANEL_MESSAGES: Readonly<Record<PanelMessageKey, PanelMessage>> = {
    filteredOut: {
        description: 'ssm5:dtcs.filteredOut.description',
        icon: 'filter',
        title: 'ssm5:dtcs.filteredOut.title',
    },
    loadFailed: {
        action: 'retry',
        description: 'ssm5:dtcs.loadFailed.description',
        icon: 'search-off',
        title: 'ssm5:dtcs.loadFailed.title',
    },
    noDtcs: { icon: 'search-off', title: 'ssm5:dtcs.noDtcs.title' },
    unavailable: {
        action: 'reconnect',
        description: 'ssm5:dtcs.unavailable.description',
        icon: 'search-off',
        title: 'ssm5:dtcs.unavailable.title',
    },
};

const useStyles = createUseStyles({
    // The design draws the 1px left border of the panel inside its 440px, so each block takes it off its
    // left padding of 16.
    content: {
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        gap: 16,
        padding: '16px 16px 16px 15px',
    },
    filters: {
        alignItems: 'center',
        display: 'flex',
        gap: 8,
        padding: '16px 16px 16px 15px',
    },
    header: {
        alignItems: 'center',
        display: 'flex',
        gap: 8,
        justifyContent: 'space-between',
        padding: '16px 16px 8px 15px',
    },
    // The title and the filters stay in view while the DTCs scroll under them.
    heading: {
        backgroundColor: colors.grayOne,
        position: 'sticky',
        top: 0,
        zIndex: 1,
    },
    // The shared aside brings the gray column with its left border and its own scroll. Its width and
    // padding are overridden at a higher specificity, so the result does not depend on the sheet order.
    panel: {
        '&&': {
            padding: 0,
            width: 440,
        },
    },
    switches: {
        alignItems: 'center',
        display: 'flex',
        flex: 1,
        gap: 16,
    },
});

/**
 * The Status and DTCs side panel of an SSM5 session: the DTCs the read found, grouped by module and
 * narrowed by the historical and secondary filters, or the reason there is none to list. The filters
 * work only once the read found DTCs; a recoverable failure offers to read again while the connection
 * is open, and a failure no retry recovers from offers to reconnect.
 *
 * @param props - {@link Ssm5StatusPanelProps}
 * @returns the panel
 */
export function Ssm5StatusPanel({
    canRetry,
    dtcs: { retry, state },
    filters,
    onFilterChange,
    onOpenModule,
    onReconnect,
    vin,
}: Ssm5StatusPanelProps) {
    const classes = useStyles();
    const { t } = useTranslation();
    const modules = state.status === 'loaded' ? state.readout.modules : [];
    const shownModules = filterDtcModules(modules, filters);
    const areFiltersEnabled = modules.length > 0;
    const messageKey = panelMessageKey(state, shownModules);
    const message = messageKey && PANEL_MESSAGES[messageKey];
    const offer = message ? panelOffer(message, canRetry, { reconnect: onReconnect, retry }) : undefined;

    return (
        <DetailViewAside classNames={classes.panel}>
            <div className={classes.heading}>
                <div className={classes.header}>
                    <Ssm5SectionTitle>{t('ssm5:dtcs.title')}</Ssm5SectionTitle>
                    {state.status === 'loaded' && state.readout.tripTime && (
                        <Ssm5TripTime tripTime={state.readout.tripTime} />
                    )}
                </div>
                <div className={classes.filters}>
                    <Typography variant="caption">{t('ssm5:dtcs.filters')}</Typography>
                    <div className={classes.switches}>
                        {DTC_FILTERS.map((filter) => (
                            <Ssm5DtcFilterSwitch
                                filter={filter}
                                isChecked={filters[filter.key]}
                                isEnabled={areFiltersEnabled}
                                key={filter.key}
                                onChange={onFilterChange}
                            />
                        ))}
                    </div>
                    <Typography color="textDarkSecondary" variant="caption">
                        {t('ssm5:dtcs.count', { count: countDtcs(shownModules) })}
                    </Typography>
                </div>
            </div>
            <div className={classes.content}>
                {message && (
                    <Ssm5PanelMessage
                        action={
                            offer && (
                                <Button
                                    onPress={offer.handlePress}
                                    style={ssm5ControlStyles.primaryButton}
                                    title={t(offer.title)}
                                    titleStyle={ssm5TextStyles.primaryButtonTitle}
                                    variant="primary-blue-outline"
                                />
                            )
                        }
                        description={message.description && t(message.description)}
                        icon={message.icon}
                        title={t(message.title)}
                    />
                )}
                {!message &&
                    shownModules.map((module) => (
                        <Ssm5DtcModuleCard key={module.system} module={module} onOpen={onOpenModule} vin={vin} />
                    ))}
            </div>
        </DetailViewAside>
    );
}

/**
 * Why the panel body lists no DTC, if it lists none: the read failed, found none, or the filters hide
 * them all. A read in flight shows an empty body.
 *
 * @param state - where the read stands
 * @param shownModules - the modules the filters let through
 * @returns the message to show, or undefined when there is none
 */
function panelMessageKey(state: Ssm5DtcsState, shownModules: readonly Ssm5DtcModule[]): PanelMessageKey | undefined {
    switch (state.status) {
        case 'failed':
            return state.isRecoverable ? 'loadFailed' : 'unavailable';
        case 'idle':
        case 'loading':
            return undefined;
        case 'loaded':
            if (state.readout.modules.length === 0) {
                return 'noDtcs';
            }

            return shownModules.length === 0 ? 'filteredOut' : undefined;
    }
}

/**
 * The offer a message makes, when the panel can carry its action out: a retry needs the connection open.
 *
 * @param message - the message shown
 * @param canRetry - whether a failed read can be retried
 * @param handlers - what carries each action out
 * @returns the offer, or undefined when the message offers nothing the panel can carry out
 */
function panelOffer(
    message: PanelMessage,
    canRetry: boolean,
    handlers: Readonly<Record<PanelAction, () => void>>
): PanelOffer | undefined {
    const { action } = message;

    if (action === undefined || (PANEL_ACTIONS[action].needsConnection && !canRetry)) {
        return undefined;
    }

    return { handlePress: handlers[action], title: PANEL_ACTIONS[action].title };
}
